<template>
  <v-container fluid>
    <v-card class="sv-card pa-4">
      <v-row no-gutters>
        <data-table
          ref="table-hoc"
          :height-offset="252"
          :filters.sync="filters"
          :schema="filterSchema"
          :headers="headers"
          :count="count"
          :data-table-props="{ sortBy: 'id' }"
          :loading="loading"
          :items="users"
          @fetch="loadUsers"
        >
          <template v-slot:before-filter>
            <v-btn
              v-if="$canAdd()"
              color="primary"
              depressed
              class="mr-4"
              @click="addItem()"
            >
              <v-icon>mdi-plus</v-icon>
              Пользователь
            </v-btn>
          </template>

          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.email="{ item }">
            <span v-if="item.blocked_at == null">
              {{ item.email }}
            </span>
            <div v-else class="d-flex justify-start">
              <span>{{ item.email }}</span>
              <v-icon class="sv-danger ml-2">mdi-account-cancel-outline</v-icon>
            </div>
          </template>

          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.roles="{ item: { roles } }">
            <div class="py-1">
              <badge
                v-for="(role, id) in rolesView(roles)"
                :key="id"
                :text="role"
              />
            </div>
          </template>

          <!-- eslint-disable-next-line vue/valid-v-slot -->
          <template #item.actions="{ item }">
            <action-col>
              <action-col-btn
                v-if="$canViewHistory()"
                :id="`btn-view-history-${item.id}`"
                tooltip="История изменений"
                @click="showHistory(item.id)"
              >
                mdi-history
              </action-col-btn>
              <action-col-btn
                :id="`btn-edit-${item.id}`"
                :tooltip="$canEdit() ? 'Изменить' : 'Просмотр'"
                @click="editUser(item.id)"
              >
                {{ $canEdit() ? "mdi-pencil-outline" : "mdi-eye-outline" }}
              </action-col-btn>
              <action-col-btn
                v-if="$canDelete()"
                :id="`btn-delete-${item.id}`"
                :disabled="!item.can_delete"
                tooltip="Удалить"
                @click="deleteUser(item.id)"
              >
                mdi-close
              </action-col-btn>
            </action-col>
          </template>
        </data-table>
      </v-row>
      <user-dialog
        v-model="userDialog"
        :user-id="selectedUserId"
        :roles="roles"
        :readonly="!$canEdit()"
        @close="selectedUserId = null"
        @apply="emitFetch"
      />
      <history-object-modal
        v-if="$canViewHistory()"
        v-model="historyDialog"
        :item-object-id="selectedUserId"
        scope-name="Users"
      />
    </v-card>
  </v-container>
</template>
<script>
import CheckPermissionMixin from "@/mixins/CheckPermissionMixin";
import NotificationMixin from "@/mixins/NotificationMixin";
import DialogMixin from "@/mixins/DialogMixin";
import ResizeMixin from "@/mixins/ResizeMixin";
import QueryMixin from "@/mixins/QueryMixin";

import SearchFilter from "@/components/Filters/SearchFilter";
import DatetimeRangeFilter from "@/components/Filters/DatetimeRangeFilter";
import SelectFilter from "@/components/Filters/SelectFilter";
import DataTable from "@/components/DataTable.vue";
import Badge from "@/components/Badge.vue";

import UserDialog from "./UserDialog.vue";
import HistoryObjectModal from "@/components/HistoryObjectModal.vue";

import Repository from "@/api/users";
import RoleRepository from "@/api/roles";

const maxRoleViewCount = 3;

export default {
  components: { UserDialog, DataTable, HistoryObjectModal, Badge },
  mixins: [
    CheckPermissionMixin,
    NotificationMixin,
    DialogMixin,
    QueryMixin,
    ResizeMixin
  ],
  data: () => ({
    selectedUserId: null,
    historyTable: "users",
    count: 0,
    options: {},
    loading: false,
    userDialog: false,
    filterDialog: false,
    historyDialog: false,
    filters: {},
    users: [],
    roles: []
  }),
  computed: {
    headers() {
      return [
        { text: "ID", value: "id" },
        { text: "Имя", value: "name" },
        { text: "Email", value: "email" },
        { text: "Роли", value: "roles", sortable: false },
        { text: "Дата создания", value: "created_at", width: "160px" },
        {
          text: "Действия",
          value: "actions",
          sortable: false,
          align: "center",
          width: "155px"
        }
      ];
    },
    filterSchema() {
      const { roles } = this;
      return {
        residentData: {
          filters: {
            name: {
              component: SearchFilter,
              title: "Имя",
              filterBind: { showMethod: false, defaultLike: true },
              like: true
            },
            email: {
              component: SearchFilter,
              title: "Email",
              filterBind: { showMethod: false }
            },
            roles: {
              component: SelectFilter,
              title: "Роли",
              filterBind: { items: roles, valueItem: "id", textItem: "title" }
            },
            createdAt: {
              title: "Дата создания",
              component: DatetimeRangeFilter
            }
          }
        }
      };
    }
  },

  created() {
    this.loadRoles();
  },

  methods: {
    async loadUsers(params) {
      try {
        this.loading = true;
        const { data } = await Repository.getList(params);
        this.users = data.users;
        this.count = data.count;
      } catch (error) {
        return true;
      } finally {
        this.loading = false;
      }
    },
    async loadRoles() {
      try {
        const { data } = await RoleRepository.getAllRoles();
        this.roles = data;
      } catch (error) {
        return true;
      }
    },
    addItem() {
      this.selectedUserId = null;
      this.userDialog = true;
    },
    editUser(id) {
      this.selectedUserId = id;
      this.userDialog = true;
    },
    showHistory(id) {
      this.selectedUserId = id;
      this.historyDialog = true;
    },
    rolesView(roles) {
      if (roles.length > maxRoleViewCount) {
        return roles
          .slice(0, maxRoleViewCount - 1)
          .concat(`ещё ${roles.length - maxRoleViewCount + 1}...`);
      } else {
        return roles;
      }
    },
    progressCallback({ loaded, total }) {
      this.percent = Math.round((loaded * 100) / total);
      if (this.percent == 100) {
        this.indeterminate = true;
      }
    },
    async deleteUser(id) {
      const confirm = await this.confirm(
        "Удаление пользователя",
        "Вы действительно хотите удалить этого пользователя? Это действие нельзя отменить!",
        {
          width: 550
        }
      );
      if (!confirm) return;
      try {
        const { data } = await Repository.delete(id);
        this.emitFetch();
        this.sendAlert(data.alert);
      } catch (error) {
        return true;
      }
    },
    emitFetch() {
      this.$refs["table-hoc"].fetchData();
    }
  }
};
</script>
<style lang="scss" scoped>
@import "../../styles/branding.scss";
.sv-disabled {
  color: $brand-control-icon;
}
.sv-enabled {
  color: $brand-status-success;
}
.sv-danger {
  color: $brand-status-danger;
}
</style>
